import React, { useMemo, useEffect, useState } from 'react';
/* Fix: Import from react-router instead of react-router-dom for v7 unified package support */
import { useParams } from 'react-router';
import { Play, Infinity, ChevronRight } from 'lucide-react';
import { TRANSLATIONS, FALLBACK_LANG, TMDB_IMAGE_BASE, SUPPORTED_LANGS } from '../constants';
import { SupportedLang, HydratedMovie } from '../types';
import { getCurrentDay, preloadImage, getTimeUntilNextMidnight } from '../utils/gameUtils';
import { useLanguageNavigate } from '../App';
import CinemaDivider from './CinemaDivider';

const Home: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = window.location.pathname;
  const parts = location.split('/').filter(Boolean);
  const lang = (SUPPORTED_LANGS.includes(parts[0] as SupportedLang) && parts[0] !== 'en')
    ? (parts[0] as SupportedLang)
    : FALLBACK_LANG;

  const langNavigate = useLanguageNavigate();

  const [currentDay, setCurrentDay] = useState(getCurrentDay());
  const [countdown, setCountdown] = useState(getTimeUntilNextMidnight());

  useEffect(() => {
    const timer = setInterval(() => {
      const nextTime = getTimeUntilNextMidnight();
      setCountdown(nextTime);
      const dayNow = getCurrentDay();
      if (dayNow !== currentDay) setCurrentDay(dayNow);
    }, 1000);
    return () => clearInterval(timer);
  }, [currentDay]);

  const isDailyCompleted = useMemo(() => {
    const saved = localStorage.getItem(`MovieRiddle_Daily_Index_${currentDay}`);
    if (!saved) return false;
    const state = JSON.parse(saved);
    return state.status === 'won' || state.status === 'lost';
  }, [currentDay]);

  useEffect(() => {
    const fetchAndPreload = async () => {
      try {
        const response = await fetch('/movies.json');
        const moviesList: (HydratedMovie | number)[] = await response.json();
        const movieEntry = moviesList[currentDay % moviesList.length];
        if (typeof movieEntry === 'object' && movieEntry !== null) {
          const framesToPreload = movieEntry.images.slice(0, 2);
          framesToPreload.forEach(path => preloadImage(`${TMDB_IMAGE_BASE}${path}`));
        }
      } catch (e) {
        console.warn("Preload failed", e);
      }
    };
    fetchAndPreload();
  }, [currentDay]);

  return (
    <div className="relative flex-1 w-full flex flex-col items-center justify-center p-4 sm:p-10 text-center overflow-hidden min-h-[calc(100vh-80px)] lg:min-h-0 lg:py-12">
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center scale-110 opacity-10 dark:opacity-10 transition-opacity duration-2000" style={{ backgroundImage: `url('https://image.tmdb.org/t/p/original/r291vYpSjfsSZXR76bghviLMpPR.jpg')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-off-white/95 via-off-white/80 to-off-white dark:from-midnight/95 dark:via-midnight/80 dark:to-midnight" />
      </div>

      <div className="relative z-10 space-y-2 sm:space-y-4 lg:space-y-6 w-full max-w-5xl animate-fade-in flex flex-col items-center">
        <div className="space-y-4 max-w-4xl px-4">
          <h1 className="text-4xl sm:text-6xl md:text-6xl lg:text-7xl font-heading uppercase tracking-[0.05em] sm:tracking-[0.1em] leading-[0.95] text-ink dark:text-off-white drop-shadow-sm">
            {TRANSLATIONS.homeTitle[lang]}
          </h1>
          <p className="text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[9px] sm:text-[11px] lg:text-[12px] pt-1 px-4 leading-relaxed">
            {TRANSLATIONS.homeSubtitle[lang]}
          </p>
        </div>

        <CinemaDivider />

        <div className="flex flex-col md:flex-row gap-4 sm:gap-6 lg:gap-6 w-full max-w-sm sm:max-w-3xl lg:max-w-[840px] mx-auto px-4 justify-center items-stretch">
          <button 
            onClick={() => langNavigate('daily')}
            className="flex-1 group relative flex items-center justify-between bg-white dark:bg-zinc-900/60 border border-prestige-gold p-5 sm:p-7 lg:p-5 rounded-[16px] transition-all duration-500 shadow-md hover:shadow-xl hover:shadow-prestige-gold/20 hover:-translate-y-1 active:scale-[0.98] cursor-pointer min-h-[90px] sm:min-h-[110px]"
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-10 lg:h-10 bg-prestige-gold rounded-[10px] flex items-center justify-center text-midnight shadow-lg shadow-prestige-gold/30 group-hover:scale-105 transition-transform duration-300 shrink-0">
                <Play className="w-4 h-4 sm:w-5 sm:h-5 lg:w-4 lg:h-4 fill-current" />
              </div>
              <div className="text-left flex flex-col justify-center">
                <span className="block text-lg sm:text-xl lg:text-base font-heading tracking-wider leading-tight text-ink dark:text-off-white uppercase">
                  {TRANSLATIONS.dailyPlay[lang]}
                </span>
                <div className="flex items-center gap-3 mt-1.5">
                  {isDailyCompleted ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold tracking-widest text-slate-400">{TRANSLATIONS.nextReelLabel[lang]}</span>
                      <span className="text-[11px] font-black uppercase text-ink dark:text-off-white tabular-nums tracking-wider">{countdown}</span>
                    </div>
                  ) : (
                    <span className="text-[10px] sm:text-[11px] font-bold uppercase opacity-60 dark:opacity-50 tracking-[0.2em] text-ink dark:text-off-white">
                      {TRANSLATIONS.dayLabel[lang]} {currentDay + 1}
                    </span>
                  )}
                  {isDailyCompleted && (
                    <span className="inline-flex items-center bg-emerald/10 text-emerald border border-emerald/20 px-2 py-0.5 rounded-full text-[7px] sm:text-[8px] font-extrabold uppercase tracking-widest">
                      {TRANSLATIONS.completed[lang]}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-prestige-gold opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0" />
          </button>

          <button 
            onClick={() => langNavigate('infinite')}
            className="flex-1 group relative flex items-center justify-between bg-white dark:bg-zinc-900/60 border border-prestige-gold p-5 sm:p-7 lg:p-5 rounded-[16px] transition-all duration-500 shadow-md hover:shadow-xl hover:shadow-prestige-gold/20 hover:-translate-y-1 active:scale-[0.98] cursor-pointer min-h-[90px] sm:min-h-[110px]"
          >
            <div className="flex items-center gap-3 sm:gap-4 text-left">
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-10 lg:h-10 bg-prestige-gold rounded-[10px] flex items-center justify-center text-midnight shadow-lg shadow-prestige-gold/30 group-hover:scale-105 transition-transform duration-300 shrink-0">
                <Infinity className="w-5 h-5 sm:w-6 sm:h-6 lg:w-5 lg:h-5" strokeWidth={2.5} />
              </div>
              <div className="text-left flex flex-col justify-center">
                <span className="block text-lg sm:text-xl lg:text-base font-heading tracking-wider leading-tight text-ink dark:text-off-white uppercase">
                  {TRANSLATIONS.infinitePlay[lang]}
                </span>
                <div className="flex items-center mt-1.5">
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase opacity-60 dark:opacity-50 tracking-[0.2em] text-ink dark:text-off-white">
                    {TRANSLATIONS.unlimitedReelsLabel[lang]}
                  </span>
                </div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-prestige-gold opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;